import { useEffect, useState } from "react";
import {
  Table, LinkSimple, SealCheck, Sparkle, DownloadSimple, FolderOpen, Database,
  X, Lightbulb, ArrowRight,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { api } from "../lib/api";
import { fileColor } from "../lib/files";

const SECTIONS = [
  { id: "sorgenti", label: "Sorgenti dati", icon: Table, color: "#0B78B3" },
  { id: "abbinamento", label: "Motore di abbinamento", icon: LinkSimple, color: "#7c3aed" },
  { id: "risultato", label: "Risultato abbinato", icon: SealCheck, color: "#0f9d58" },
  { id: "tools", label: "Playground verifiche", icon: Sparkle, color: "#e8590c" },
  { id: "export", label: "Export", icon: DownloadSimple, color: "#2b8a3e" },
  { id: "lavori", label: "Lavori · Modelli · Area comune", icon: FolderOpen, color: "#1971c2" },
  { id: "dati", label: "Dove sono i dati?", icon: Database, color: "#c2255c" },
];

function Tip({ children }) {
  return (
    <div className="flex items-start gap-2 rounded-lg bg-[hsl(var(--gold)/0.12)] border border-[hsl(var(--gold)/0.4)] p-3 text-sm">
      <Lightbulb size={18} weight="fill" className="text-[hsl(var(--gold))] mt-0.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

function Pill({ color, children }) {
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: `${color}22`, color }}>{children}</span>;
}

export function GuideDialog({ open, onClose }) {
  const [mode, setMode] = useState("semplice");
  const [section, setSection] = useState("sorgenti");
  const [cats, setCats] = useState(null);

  useEffect(() => {
    if (open && !cats) api.getModules().then((d) => setCats(d.categories)).catch(() => setCats([]));
  }, [open]);

  const adv = mode === "avanzata";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-popover border-border block w-[95vw] max-w-4xl p-0 overflow-hidden [&>button]:hidden" data-testid="guide-dialog">
        <DialogTitle className="sr-only">Guida a RowTool</DialogTitle>
        <DialogDescription className="sr-only">Mini tutorial di RowTool con modalità Semplice e Avanzata.</DialogDescription>
        {/* Header */}
        <div className="flex items-center justify-between gap-2 flex-wrap px-4 sm:px-6 py-4 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent))] text-white">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/rowtool-logo.png" alt="RowTool" className="w-10 h-10 rounded-xl bg-white/15 p-1 shrink-0" />
            <div className="min-w-0">
              <div className="font-head font-extrabold text-lg leading-none">Guida a RowTool</div>
              <div className="text-xs text-white/80 mt-1 truncate">Incrocia i tuoi dati in pochi passi — scegli quanto entrare nel dettaglio</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Semplice / Avanzata slider */}
            <div className="relative flex items-center bg-white/15 rounded-full p-0.5 text-xs font-semibold" data-testid="guide-mode-toggle">
              <button onClick={() => setMode("semplice")} data-testid="guide-mode-semplice"
                className={`px-3 py-1 rounded-full transition-colors ${!adv ? "bg-white text-[hsl(var(--primary))]" : "text-white/90"}`}>Semplice</button>
              <button onClick={() => setMode("avanzata")} data-testid="guide-mode-avanzata"
                className={`px-3 py-1 rounded-full transition-colors ${adv ? "bg-white text-[hsl(var(--primary))]" : "text-white/90"}`}>Avanzata</button>
            </div>
            <button onClick={onClose} data-testid="guide-close-btn" className="text-white/80 hover:text-white"><X size={20} /></button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row max-h-[74vh] min-w-0">
          {/* Section wraps */}
          <div className="w-full md:w-56 min-w-0 shrink-0 border-b md:border-b-0 md:border-r border-border p-3 flex md:flex-col gap-2 overflow-x-auto md:overflow-y-auto bg-muted/30">
            {SECTIONS.map((s) => {
              const Icon = s.icon; const active = section === s.id;
              return (
                <button key={s.id} onClick={() => setSection(s.id)} data-testid={`guide-tab-${s.id}`}
                  className={`flex items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium transition-all whitespace-nowrap md:whitespace-normal shrink-0 md:shrink ${active ? "text-white shadow" : "text-foreground hover:bg-muted"}`}
                  style={active ? { background: s.color } : {}}>
                  <Icon size={18} weight={active ? "fill" : "regular"} style={active ? {} : { color: s.color }} />
                  <span>{s.label}</span>
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 overflow-y-auto p-6 space-y-4 text-sm leading-relaxed" data-testid="guide-content">
            {section === "sorgenti" && <Sorgenti adv={adv} />}
            {section === "abbinamento" && <Abbinamento adv={adv} />}
            {section === "risultato" && <Risultato adv={adv} />}
            {section === "tools" && <Tools adv={adv} cats={cats} />}
            {section === "export" && <Export adv={adv} />}
            {section === "lavori" && <Lavori />}
            {section === "dati" && <Dati adv={adv} />}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function H({ children }) { return <h3 className="font-head font-bold text-base text-foreground">{children}</h3>; }
function P({ children }) { return <p className="text-muted-foreground">{children}</p>; }

function ConfrontTypes() {
  return (
    <div className="rounded-lg border border-border p-3 space-y-1.5">
      <div className="text-xs font-semibold mb-1">I tipi di confronto (le pastiglie azzurre nel livello):</div>
      <ul className="space-y-1">
        <li><Pill color="#0891b2">esatta</Pill> <span className="text-muted-foreground">— i valori devono essere identici.</span></li>
        <li><Pill color="#0891b2">no maiuscole</Pill> <span className="text-muted-foreground">— ignora maiuscole/minuscole (Rossi = ROSSI).</span></li>
        <li><Pill color="#0891b2">no speciali</Pill> <span className="text-muted-foreground">— ignora punteggiatura, accenti e spazi extra.</span></li>
        <li><Pill color="#0891b2">contiene</Pill> <span className="text-muted-foreground">— basta che un valore sia contenuto nell'altro (utile per descrizioni). La direzione è mostrata dal Chip "<em>… contiene …</em>": la fonte più a sinistra è il contenitore.</span></li>
        <li><Pill color="#0891b2">assomiglia</Pill> <span className="text-muted-foreground">— corrispondenza "somigliante" che tollera piccole differenze/errori di battitura, regolabile con la <strong>Soglia similarità</strong>.</span></li>
      </ul>
    </div>
  );
}

function Sorgenti({ adv }) {
  return (
    <>
      <H>Passo 1 · Sorgenti dati 📄</H>
      <P>Trascina fino a <strong>3 file</strong> nel riquadro (oppure usa <em>Sfoglia</em> / <em>Incolla dati</em>). Vanno bene <strong>Excel</strong>, <strong>CSV</strong>, <strong>PDF</strong> e testo incollato. Ogni file diventa una <strong>Fonte</strong> colorata:</P>
      <div className="flex gap-2 flex-wrap">
        <Pill color={fileColor(1)}>Fonte 1</Pill><Pill color={fileColor(2)}>Fonte 2</Pill><Pill color={fileColor(3)}>Fonte 3</Pill>
      </div>
      <P>Puoi <strong>rinominare</strong> una fonte cliccando sul nome, vedi quante <strong>righe/colonne</strong> ha e la rimuovi col cestino.</P>
      {!adv ? (
        <Tip>Il numero accanto al nome (es. <strong>120 righe</strong>) indica quanti dati userà l'abbinamento. Se compare il badge <strong>filtrato</strong> o <strong>deduplicato</strong> stai usando solo una parte dei dati.</Tip>
      ) : (
        <>
          <H>Filtra e Rimuovi duplicati</H>
          <P>Col pulsante <strong>Filtra</strong> apri un'anteprima stile Excel: spunti solo i valori che ti interessano (i filtri sono <em>a cascata</em>, si aggiornano tra loro). Puoi anche attivare <strong>Rimuovi righe duplicate</strong> scegliendo su quali colonne considerare "uguale" una riga.</P>
          <P>Filtro e deduplica <strong>incidono sull'abbinamento e sui conteggi</strong>: sarà usata solo la parte di dati risultante. La card mostra <strong>N / M righe</strong> e i badge <em>filtrato</em> / <em>deduplicato</em>.</P>
          <H>PDF: anteprima editabile</H>
          <P>Da un PDF RowTool estrae le tabelle e apre un'<strong>anteprima modificabile</strong>: scegli la tabella giusta, correggi celle e intestazioni, <strong>unisci</strong> o <strong>dividi</strong> colonne, aggiungi/elimina righe, poi conferma l'import.</P>
        </>
      )}
    </>
  );
}

function Abbinamento({ adv }) {
  return (
    <>
      <H>Passo 2 · Motore di abbinamento 🔗</H>
      <P>Il <strong>Motore di abbinamento</strong> collega, per ogni riga, la corrispondenza tra le fonti. Scegli le <strong>colonne chiave</strong> nel livello (le pastiglie mostrano anche la % di quanto sono compilate), poi premi <strong>Esegui abbinamento</strong>. Puoi anche usare <strong>Suggerisci chiavi</strong> per una proposta automatica.</P>
      <ConfrontTypes />
      {!adv ? (
        <Tip>Un solo <strong>livello (L1)</strong> con la tua colonna chiave (es. il Nominativo) basta per iniziare. Aggiungi le pastiglie di confronto qui sopra per rendere l'abbinamento più tollerante (es. <strong>assomiglia</strong> per gestire piccoli errori di battitura).</Tip>
      ) : (
        <>
          <H>Livelli (L1, L2, …)</H>
          <P>Imposti <strong>più livelli</strong> in ordine con <strong>Aggiungi livello</strong>: <strong>L1 è la chiave principale</strong>; i livelli successivi <em>restringono</em> i candidati per evitare falsi positivi. L'abbinamento è <strong>univoco</strong> (un record già usato non viene riabbinato). Riordini i livelli con le frecce o trascinandoli (drag &amp; drop).</P>
          <H>Direzione delle fonti e "contiene"</H>
          <P>In ogni livello sposti le fonti lateralmente con le frecce ◀ ▶: l'ordine imposta la direzione del confronto <strong>contiene</strong> (la fonte più a sinistra è il "contenitore"). Il Chip <em>«Fonte A contiene Fonte B»</em> mostra la direzione attiva.</P>
          <H>Soglia similarità (assomiglia)</H>
          <P>Attivando <strong>assomiglia</strong> compare lo slider <strong>Soglia similarità</strong> (50–100%): più alta = più severo (solo valori molto simili), più bassa = più permissivo.</P>
        </>
      )}
    </>
  );
}

function Rating({ color, children }) {
  return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold text-white" style={{ background: color }}>{children}</span>;
}

function Risultato({ adv }) {
  return (
    <>
      <H>Passo 3 · Risultato abbinato ✅</H>
      <P>Dopo l'abbinamento ogni riga riceve uno <strong>stato</strong> (il Chip colorato a inizio riga). Puoi filtrare la tabella con i <strong>chip in alto</strong>, che riportano anche i conteggi:</P>
      <div className="flex gap-1.5 flex-wrap">
        <Rating color="#c99700">Gold</Rating>
        <Rating color="#8a8f98">Silver</Rating>
        <Rating color="#a5642e">Bronze</Rating>
        <Rating color="#c92a2a">Incerto</Rating>
        <Rating color="#1971c2">Manuale</Rating>
        <Rating color="#495057">Non abbinato</Rating>
        <Rating color="#adb5bd">Ignorata</Rating>
      </div>
      {!adv ? (
        <>
          <P><strong>Gold</strong> = corrispondenza perfetta; <strong>Silver</strong>/<strong>Bronze</strong> = via via meno sicura; <strong>Incerto</strong> = da controllare; <strong>Non abbinato</strong> = riga rimasta sola; <strong>Manuale</strong> = collegata a mano da te; <strong>Ignorata</strong> = esclusa dai conteggi.</P>
          <P>Clicca il <strong>Chip</strong> dello stato per correggerlo a mano, oppure seleziona più righe e usa la barra "<em>cambia stato</em>". Il pulsante <strong>Abbina</strong> collega a mano le righe <em>Non abbinate</em>.</P>
          <Tip>Le colonne di <strong>verifica</strong> (create dai tool) hanno celle a <strong>colore dinamico</strong>: <span className="text-[hsl(142,55%,26%)] font-semibold">verde</span> = ok, <span className="text-[hsl(30,80%,32%)] font-semibold">arancio</span> = piccola differenza, <span className="text-[hsl(0,68%,40%)] font-semibold">rosso</span> = differenza rilevante.</Tip>
        </>
      ) : (
        <>
          <H>Gestire le colonne</H>
          <P>Col pulsante <strong>Colonne (n/m)</strong> scegli quali mostrare/nascondere: ogni colonna è etichettata per fonte, o come <em>verifica</em> (dai tool) o <em>tecnica</em>. Le colonne visibili sono quelle che finiscono nell'export.</P>
          <H>Riepilogo verifiche</H>
          <P>Il pannello <strong>Riepilogo verifiche</strong> raccoglie i risultati dei tool applicati; cliccando una scheda con differenze filtri direttamente le righe interessate.</P>
          <H>Abbinamento manuale multi-fonte</H>
          <P>Anche una riga già abbinata a una fonte resta disponibile per <strong>Abbina</strong> se un'altra fonte ha ancora orfani: scegli la fonte da collegare, cerca su tutte le colonne (con ordinamento per pertinenza) e completi senza perdere gli abbinamenti già fatti. Lo stato diventa <strong>Manuale</strong>.</P>
          <H>Anteprima 500 righe</H>
          <P>La griglia mostra fino a <strong>500 righe</strong>; usa <strong>Mostra tutte</strong> per vederle tutte. L'export contiene comunque sempre tutte le righe filtrate.</P>
        </>
      )}
    </>
  );
}

function Tools({ adv, cats }) {
  return (
    <>
      <H>Passo 4 · Playground verifiche 🧩</H>
      <P>I <strong>tool</strong> creano <strong>nuove colonne</strong> (esportabili) a partire dai dati abbinati: differenze, somme, confronti, ecc. Si applicano alle <strong>righe selezionate</strong> oppure a tutte le righe filtrate.</P>
      {!adv ? (
        <>
          <P>Nel <strong>Playground verifiche</strong> trovi i tool pronti, raggruppati per categoria, con una <strong>ricerca</strong>. Le colonne di verifica compaiono in testa alla tabella <em>Risultato abbinato</em> con celle a colore dinamico (verde / arancio / rosso).</P>
          <P><strong>Totali per data</strong> (in Contabilità): scegli la colonna data, le colonne da sommare (es. premi) e il periodo — giorno, quindicina (1-15 / 16-fine mese), mese o anno. Dopo ogni periodo viene inserita una <strong>riga di totale</strong> e nel <strong>Riepilogo verifiche</strong> compare la tabella dei totali.</P>
          <P><strong>Formule stile Excel</strong>: nel box <em>Crea un tool con l'AI</em> inizia a scrivere con <strong>=</strong>. Ti vengono suggerite le funzioni (SE, SOMMA, ARROTONDA, ANNO, ARRAYFORMULA…) e puoi <strong>cliccare l'intestazione di una colonna</strong> della tabella per inserirla, es. <em>{'=SE({F1_Premio} < 100; "Basso"; {F1_Premio})'}</em>. Separatore degli argomenti: <strong>;</strong></P>
          <Tip>Non sai quale usare? In <strong>Crea un tool con l'AI</strong> scrivi in linguaggio naturale cosa ti serve (es. "differenza in giorni tra le due date") e RowTool costruisce il tool per te.</Tip>
        </>
      ) : (
        <>
          <H>Come lavora "Crea un tool con l'AI": i mattoncini "Lego"</H>
          <P>L'AI <strong>non scrive codice libero</strong>: assembla dei <strong>mattoncini pre-costruiti e sicuri</strong>. Descrivi l'obiettivo e sceglie il mattoncino giusto, poi ti chiede in un pop-up quali colonne usare. Ecco quelli realmente disponibili adesso:</P>
          {!cats && <P>Carico l'elenco…</P>}
          {cats && cats.map((c) => (
            <div key={c.category} className="rounded-lg border border-border p-3">
              <div className="font-semibold text-sm mb-2">{c.label} <span className="text-muted-foreground font-normal">· {c.modules.length}</span></div>
              <ul className="space-y-1.5">
                {c.modules.map((m) => (
                  <li key={m.op} className="flex items-start gap-2" data-testid={`guide-module-${m.op}`}>
                    <ArrowRight size={14} className="text-[hsl(var(--accent))] mt-1 shrink-0" />
                    <span><strong>{m.label}</strong> <span className="text-muted-foreground">— {m.description}</span></span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <H>Totali per data e formule</H>
          <P><strong>Totali per data</strong> (mattoncino <em>date_group_sum</em>) somma le colonne scelte per giorno, quindicina, mese o anno: inserisce righe di totale (Chip <strong>Totale</strong>, escluse da conteggi e altri tool) e una tabella nel Riepilogo verifiche. La <strong>modalità formula</strong> (scrivi <strong>=</strong> nel box AI) usa un motore sicuro con funzioni italiane: SE, SE.ERRORE, E, O, NON, SOMMA, MEDIA, MIN, MAX, ARROTONDA, ASS, CONCATENA, SINISTRA, DESTRA, LUNGHEZZA, MAIUSC, MINUSC, ANNO, MESE, GIORNO, VAL.VUOTO, ARRAYFORMULA; operatori + − * / ^ &amp; e confronti = &lt;&gt; &lt; &gt;. Errori come #VALORE! o #DIV/0! compaiono nella cella.</P>
          <Tip>Puoi <strong>salvare un tool AI nella Libreria</strong> e richiamarlo in altri lavori, oppure <strong>modificarlo</strong> (dal menu ⋮) per rigenerarlo con nuove istruzioni.</Tip>
        </>
      )}
    </>
  );
}

function Export({ adv }) {
  return (
    <>
      <H>Porta fuori i risultati 📤</H>
      <P>Con i pulsanti in alto nel <em>Risultato abbinato</em> esporti ciò che vedi: <strong>Excel</strong>, <strong>PDF</strong>, <strong>CSV</strong>, <strong>JSON</strong> o <strong>Copia</strong>. L'export rispetta le <strong>colonne visibili</strong>, i <strong>filtri attivi</strong> e i <strong>colori</strong> delle verifiche.</P>
      {!adv ? (
        <Tip>La griglia mostra un'anteprima fino a <strong>500 righe</strong> (usa <strong>Mostra tutte</strong> per vederle tutte), ma <strong>l'export contiene sempre tutte</strong> le righe filtrate.</Tip>
      ) : (
        <>
          <H>Excel/PDF con colori</H>
          <P>Le colonne di verifica vengono esportate con la <strong>stessa formattazione condizionale</strong> della tabella (verde/arancio/rosso, oppure Uguale/Diverso, soglie…), con intestazioni in blu.</P>
          <H>Dividi in ZIP + indice</H>
          <P>Il mattoncino <strong>Dividi in ZIP</strong> crea un file per ogni valore di una colonna (es. una scheda per Compagnia), nel formato scelto, con un <strong>file indice</strong> Excel opzionale che riepiloga nome file, valore, n° righe e <strong>aggregazioni per gruppo</strong> (somma/conteggio/min/max/media).</P>
        </>
      )}
    </>
  );
}

function Lavori() {
  return (
    <>
      <H>Lavori, Modelli e Area comune 🗂️</H>
      <P>Ogni progetto è un <strong>Lavoro</strong>. Alla creazione dai un <strong>Nome</strong>, una <strong>Categoria</strong> e un <strong>Soggetto</strong>: compaiono come <strong>pastiglie colorate</strong> accanto al nome e nel menu <em>Lavori</em>, dove puoi <strong>filtrarli</strong> per ritrovarli al volo.</P>
      <H>Modelli</H>
      <P>Con <strong>Modelli</strong> salvi una configurazione riutilizzabile: livelli del motore di abbinamento, colonne di output e filtri, oppure l'intero progetto. Al richiamo un <strong>banner</strong> ti ricorda quali fonti/colonne servono; i modelli incompatibili sono in grigio ma <em>forzabili</em>.</P>
      <H>Area comune</H>
      <P>È uno spazio condiviso con <strong>un solo lavoro alla volta</strong>, protetto da <strong>password</strong>. Chi pubblica sceglie la password; gli altri accedono con quella. Chi ha bisogno dello spazio può <strong>chiedere al creatore di liberarlo</strong>: il creatore riceve un <strong>avviso rosso</strong> sul tasto <em>Area comune</em> e può liberare l'area o azzerare gli avvisi.</P>
    </>
  );
}

function Dati({ adv }) {
  return (
    <>
      <H>Dove sono i miei dati? 💾</H>
      <P>I tuoi lavori sono legati a un <strong>utente temporaneo</strong> salvato in questo browser (nessun login). Finché usi lo stesso browser ritrovi tutto: file, livelli, risultati e tool. I dati sono sul <strong>server di RowTool</strong>, non solo nel browser.</P>
      <Tip>
        <strong>Consiglio importante:</strong> esporta ogni tanto il lavoro col pulsante <DownloadSimple size={14} className="inline mb-0.5" /> (file <strong>.rowtool</strong>) e <strong>salvalo sul tuo PC</strong>. Così hai un backup completo (file, livelli, colonne, filtri, risultati, tool) da <strong>reimportare quando vuoi</strong>, anche da un altro browser o computer.
      </Tip>
      {adv && (
        <>
          <H>Buone pratiche</H>
          <P>• Svuotando la cache o cambiando browser potresti non vedere più i lavori privati → tieni un export <strong>.rowtool</strong>.<br />• L'<strong>Area comune</strong> ospita un solo lavoro: chiudendolo i dati vengono svuotati per tutti, quindi esporta prima se ti servono.<br />• I <strong>Modelli</strong> sono utili per ripartire velocemente su nuovi file con la stessa struttura.</P>
        </>
      )}
    </>
  );
}
